import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, shareReplay, catchError } from 'rxjs/operators';
import { environment } from '@environment/environment';

/**
 * Configuration interface matching backend YAML structure
 */
export interface SpongeAtsConfig {
  ats_name: string;
  ats_key: string;
  version?: string;
  description?: string;
  authentication: {
    strategy: 'oauth2_code' | 'oauth2_client_credentials' | 'api_key' | 'basic_auth' | 'header_token';
    endpoints?: {
      authorize?: string;
      token?: string;
      refresh?: string;
    };
    credentials: Array<{
      name: string;
      label?: string;
      type?: string;
      required: boolean;
      secure?: boolean;
      help_text?: string;
    }>;
    oauth?: {
      scope?: string;
      response_type?: string;
      authorization_url?: string;
      token_url?: string;
    };
  };
  frontend?: {
    display_name?: string;
    setup_form?: {
      fields: Array<{
        name: string;
        type: string;
        label: string;
        required: boolean;
        placeholder?: string;
      }>;
    };
    oauth_flow?: {
      enabled: boolean;
      authorization_url?: string;
      callback_url?: string;
      button_text?: string;
    };
  };
}

@Injectable({
  providedIn: 'root'
})
export class SpongeConfigService {
  private readonly ENDPOINT = `${environment.SERVER_ENDPOINT}/api/sponge`;
  private configCache = new Map<string, Observable<SpongeAtsConfig>>();

  constructor(private readonly http: HttpClient) {}

  /**
   * Get configuration for a specific ATS
   * Attempts to fetch from backend, falls back to static config
   */
  public getConfig(atsKey: string): Observable<SpongeAtsConfig> {
    if (!this.configCache.has(atsKey)) {
      const config$ = this.fetchConfigFromBackend(atsKey).pipe(
        shareReplay(1)
      );
      this.configCache.set(atsKey, config$);
    }
    return this.configCache.get(atsKey)!;
  }

  /**
   * Get all available ATS integrations from backend
   * Returns list of available integrations with metadata
   * Throws error if fetch fails - caller should handle fallback
   */
  public getAvailableIntegrations(): Observable<any[]> {
    return this.http.get<any>(`${this.ENDPOINT}/integrations`).pipe(
      map(response => response.data || [])
    );
  }

  /**
   * Get all available ATS configurations (legacy method for backwards compatibility)
   */
  public getAvailableAts(): Observable<string[]> {
    return this.getAvailableIntegrations().pipe(
      map(integrations => integrations.map(i => i.ats_key))
    );
  }

  /**
   * Check if ATS requires OAuth Authorization Code flow (user redirect)
   */
  public requiresOAuth(config: SpongeAtsConfig): boolean {
    return config.authentication.strategy === 'oauth2_code' &&
           (config.frontend?.oauth_flow?.enabled ?? false);
  }

  /**
   * Check if ATS uses OAuth2 Client Credentials (server-to-server)
   * This doesn't require user redirect - backend handles token exchange
   */
  public requiresClientCredentials(config: SpongeAtsConfig): boolean {
    return config.authentication.strategy === 'oauth2_client_credentials';
  }

  /**
   * Get form fields for ATS setup
   */
  public getFormFields(config: SpongeAtsConfig): Array<any> {
    // Use frontend config if available
    if (config.frontend?.setup_form?.fields) {
      return config.frontend.setup_form.fields;
    }

    // Otherwise, generate from credentials
    return config.authentication.credentials.map(cred => ({
      name: cred.name,
      type: cred.secure ? 'password' : (cred.type || 'text'),
      label: cred.label || this.formatLabel(cred.name),
      required: cred.required,
      placeholder: cred.help_text || `Enter your ${cred.label || cred.name}`
    }));
  }

  /**
   * Clear configuration cache
   */
  public clearCache(): void {
    this.configCache.clear();
  }

  private fetchConfigFromBackend(atsKey: string): Observable<SpongeAtsConfig> {
    // Fetch configuration from backend API (YAML as source of truth)
    return this.http.get<any>(`${this.ENDPOINT}/config/${atsKey}`).pipe(
      map(response => response.data as SpongeAtsConfig),
      catchError((error) => {
        console.error(`Failed to fetch config for ${atsKey} from backend`, error);
        throw error;
      })
    );
  }

  private formatLabel(fieldName: string): string {
    return fieldName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
}
